<script setup lang="ts">
definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Service Level Agreement',
    subtitle: 'Onze afspraken over dienstverlening, beschikbaarheid en serviceniveaus',
    icon: 'bx-line-chart',
    iconColor: 'primary',
  },
})

const sections = [
  {
    title: '1. Inleiding',
    content: 'Deze Service Level Agreement (SLA) beschrijft de afspraken tussen \'t SpeelFabrik VZW (hierna: "Dienstverlener") en de Klant met betrekking tot de dienstverlening, beschikbaarheid, ondersteuning en serviceniveaus.',
  },
  {
    title: '2. Definities',
    content: `• Dienstverlener: 't SpeelFabrik VZW
• Klant: De partij die gebruik maakt van de diensten
• Diensten: De overeengekomen diensten zoals beschreven in de overeenkomst
• Uptime: De tijd dat de dienst beschikbaar en functioneel is
• Downtime: De tijd dat de dienst niet beschikbaar is
• Incident: Een gebeurtenis die de normale dienstverlening verstoort
• Werkdagen: Maandag t/m vrijdag, exclusief erkende feestdagen
• Kantooruren: 09:00 - 17:00 uur (CET/CEST)`,
  },
  {
    title: '3. Toepassingsgebied',
    content: `3.1 Deze SLA is van toepassing op alle diensten die door de Dienstverlener worden geleverd aan de Klant, tenzij anders overeengekomen.

3.2 Deze SLA is een aanvulling op de Algemene Voorwaarden en de specifieke dienstverleningsovereenkomst.

3.3 Bij tegenstrijdigheid tussen documenten geldt de volgende hiërarchie:
   1. Specifieke dienstverleningsovereenkomst
   2. Deze SLA
   3. Algemene Voorwaarden`,
  },
]

const servicePackages = [
  {
    name: 'Basic',
    availability: '95%',
    responseTime: '48 uur',
    support: 'Werkdagen 09:00-17:00',
    maintenanceWindow: 'Maandag 00:00-06:00',
    features: [
      'E-mail support',
      'Reguliere updates',
      'Standaard monitoring',
      'Maandelijkse rapportage',
    ],
  },
  {
    name: 'Professional',
    availability: '98%',
    responseTime: '24 uur',
    support: 'Werkdagen 08:00-20:00',
    maintenanceWindow: 'Zondag 02:00-06:00',
    features: [
      'E-mail & telefoon support',
      'Prioriteit updates',
      'Uitgebreide monitoring',
      'Wekelijkse rapportage',
      'Proactieve advisering',
    ],
  },
  {
    name: 'Enterprise',
    availability: '99.5%',
    responseTime: '4 uur',
    support: '24/7 (met on-call buiten kantooruren)',
    maintenanceWindow: 'Gepland in overleg',
    features: [
      'E-mail, telefoon & chat support',
      'Dedicated account manager',
      'Real-time monitoring',
      'Dagelijkse rapportage',
      'Proactieve optimalisatie',
      'Custom SLA mogelijk',
    ],
  },
]

const incidentPriorities = [
  {
    priority: 'Kritiek (P1)',
    description: 'Complete uitval van dienst, ernstige beveiligingsissue',
    responseBasic: '8 uur',
    responsePro: '4 uur',
    responseEnterprise: '1 uur',
    resolutionBasic: '48 uur',
    resolutionPro: '24 uur',
    resolutionEnterprise: '8 uur',
  },
  {
    priority: 'Hoog (P2)',
    description: 'Belangrijke functionaliteit niet beschikbaar, beperkte workaround',
    responseBasic: '24 uur',
    responsePro: '8 uur',
    responseEnterprise: '4 uur',
    resolutionBasic: '5 werkdagen',
    resolutionPro: '3 werkdagen',
    resolutionEnterprise: '24 uur',
  },
  {
    priority: 'Normaal (P3)',
    description: 'Beperkte impact, workaround beschikbaar',
    responseBasic: '48 uur',
    responsePro: '24 uur',
    responseEnterprise: '8 uur',
    resolutionBasic: '10 werkdagen',
    resolutionPro: '5 werkdagen',
    resolutionEnterprise: '3 werkdagen',
  },
  {
    priority: 'Laag (P4)',
    description: 'Vraag, verzoek om informatie, kleine bug',
    responseBasic: '5 werkdagen',
    responsePro: '3 werkdagen',
    responseEnterprise: '24 uur',
    resolutionBasic: 'Best effort',
    resolutionPro: '15 werkdagen',
    resolutionEnterprise: '10 werkdagen',
  },
]

const additionalSections = [
  {
    title: '7. Onderhoud en updates',
    content: `7.1 Gepland onderhoud
• Gepland onderhoud wordt minimaal 5 werkdagen van tevoren aangekondigd
• Onderhoud wordt uitgevoerd in het onderhoudsvenster van het servicepakket
• Bij spoedeisend onderhoud kan de aankondigingstermijn worden verkort tot 24 uur

7.2 Updates
• Beveiligingsupdates worden zo spoedig mogelijk uitgevoerd
• Functionele updates worden gepland in overleg met de Klant
• De Klant wordt geïnformeerd over belangrijke updates

7.3 Emergency maintenance
• Bij acute beveiligingsproblemen kan emergency maintenance zonder vooraankondiging plaatsvinden
• De Klant wordt zo spoedig mogelijk geïnformeerd`,
  },
  {
    title: '8. Monitoring en rapportage',
    content: `8.1 Monitoring
• De Dienstverlener monitort actief de beschikbaarheid en prestaties
• Automatische alerting bij incidenten
• Proactieve signalering van potentiële problemen (vanaf Professional)

8.2 Rapportage
De Dienstverlener levert periodieke rapportages met:
• Uptime statistieken
• Incident overzicht
• Performancegegevens
• Trendanalyses (vanaf Professional)
• Aanbevelingen voor optimalisatie (Enterprise)

8.3 Frequentie rapportage volgens servicepakket`,
  },
  {
    title: '9. Back-up en disaster recovery',
    content: `9.1 Back-ups
• Dagelijkse automatische back-ups
• Back-ups worden 30 dagen bewaard
• Wekelijkse test van back-ups

9.2 Restore procedures
• Restore op verzoek binnen 24 uur (Professional/Enterprise)
• Restore binnen 48 uur (Basic)
• Volledige disaster recovery binnen 48-72 uur afhankelijk van servicepakket

9.3 De Klant is verantwoordelijk voor het aanleveren van correcte data`,
  },
  {
    title: '10. Beveiliging',
    content: `10.1 De Dienstverlener neemt de volgende beveiligingsmaatregelen:
• SSL/TLS encryptie voor alle dataverkeer
• Firewalls en toegangscontroles
• Regelmatige security audits
• Patch management
• Intrusion detection (Professional/Enterprise)

10.2 Beveiligingsincidenten worden binnen 4 uur gemeld aan de Klant

10.3 De Klant is verantwoordelijk voor:
• Veilig beheer van inloggegevens
• Melding van verdachte activiteiten
• Naleving van beveiligingsrichtlijnen`,
  },
  {
    title: '11. Uitzonderingen en beperkingen',
    content: `11.1 Deze SLA is niet van toepassing bij downtime veroorzaakt door:
• Overmacht (zie Algemene Voorwaarden)
• Gepland onderhoud binnen het onderhoudsvenster
• Acties of nalatigheid van de Klant
• Externe factoren (DDoS-aanvallen, internetprovider issues)
• Third-party diensten waarover wij geen controle hebben

11.2 Het onderhoudsvenster telt niet mee voor de berekening van de uptime

11.3 Force majeure gebeurtenissen worden uitgesloten van SLA-verplichtingen`,
  },
  {
    title: '12. Compensatie',
    content: `12.1 Bij niet-naleving van de overeengekomen uptime heeft de Klant recht op compensatie:

Uptime < 95% (Basic): 10% korting op maandelijkse fee
Uptime < 98% (Professional): 15% korting op maandelijkse fee
Uptime < 99.5% (Enterprise): 20% korting op maandelijkse fee

12.2 Berekening uptime
Uptime % = ((Totale minuten - Downtime minuten) / Totale minuten) × 100

12.3 Compensatie wordt verrekend met de eerstvolgende factuur

12.4 Compensatie moet binnen 30 dagen na afloop van de maand worden aangevraagd

12.5 De compensatie is de enige remedie voor niet-gehaalde SLA-niveaus

12.6 Maximale compensatie per jaar: 30% van de jaarlijkse contractwaarde`,
  },
  {
    title: '13. Escalatieprocedure',
    content: `13.1 Escalatieniveaus:

Level 1: Servicedesk
• Eerste aanspreekpunt voor alle vragen en incidenten
• E-mail: contact@tspeelfabrik.be
• Telefoon: +32 474 68 10 52

Level 2: Technical Lead
• Bij complexe technische issues
• Automatische escalatie na X uur (afhankelijk van prioriteit)

Level 3: Service Manager
• Bij niet-opgeloste issues na 48 uur (P1/P2)
• Bij escalatie door Klant

Level 4: Management
• Bij structurele problemen
• Bij geschillen over SLA-naleving

13.2 De Klant kan direct escaleren bij onvoldoende reactie`,
  },
  {
    title: '14. Communicatie',
    content: `14.1 Statusupdates bij incidenten:
• P1: Elk uur tot oplossing
• P2: Elke 4 uur tot oplossing
• P3/P4: Bij statuswijziging

14.2 Communicatiekanalen:
• E-mail (primair)
• Telefoon (urgent)
• Ticketsysteem
• Statuspage (Enterprise)

14.3 De Klant wijst een contactpersoon aan voor SLA-gerelateerde zaken`,
  },
  {
    title: '15. Review en aanpassing',
    content: `15.1 Deze SLA wordt minimaal jaarlijks geëvalueerd in overleg tussen Dienstverlener en Klant

15.2 Wijzigingen in deze SLA:
• Worden minimaal 30 dagen van tevoren aangekondigd
• Treden in werking bij verlenging van de overeenkomst
• Kunnen tussentijds worden aangepast in onderling overleg

15.3 Bij significante wijzigingen heeft de Klant het recht de overeenkomst te beëindigen`,
  },
  {
    title: '16. Verantwoordelijkheden Klant',
    content: `16.1 De Klant is verantwoordelijk voor:
• Tijdige melding van incidenten met voldoende informatie
• Beschikbaarheid van contactpersonen
• Redelijke medewerking bij troubleshooting
• Naleving van gebruiksvoorwaarden
• Tijdige betaling van facturen
• Tijdige communicatie van geplande wijzigingen aan hun kant

16.2 Niet-naleving door de Klant kan leiden tot vertraging in dienstverlening`,
  },
  {
    title: '17. Slotbepalingen',
    content: `17.1 Deze SLA treedt in werking op de startdatum van de dienstverlening

17.2 Deze SLA blijft van kracht gedurende de looptijd van de overeenkomst

17.3 Vragen over deze SLA kunnen worden gesteld via: contact@tspeelfabrik.be

17.4 Laatste wijziging: ${new Date().toLocaleDateString('nl-BE')}

Contactgegevens Dienstverlener:
't SpeelFabrik VZW
Langestraat 15b
1910 Kampenhout
E-mail: contact@tspeelfabrik.be
Support: contact@tspeelfabrik.be
Telefoon: +32 474 68 10 52
Spoednummer (Enterprise): +32 474 68 10 52`,
  },
]
</script>

<template>
  <VContainer>
    <VCard
      class="pa-4 mx-auto"
      max-width="1400"
    >
      <VCardText>
        <VContainer>
          <!-- Introduction sections -->
          <div
            v-for="(section, index) in sections"
            :key="index"
            class="mb-6"
          >
            <h2 class="text-h6 mb-3">
              {{ section.title }}
            </h2>
            <p
              class="text-body-1"
              style="white-space: pre-line;"
            >
              {{ section.content }}
            </p>
          </div>

          <!-- Service Packages -->
          <div class="mb-8">
            <h2 class="text-h6 mb-4">
              4. Servicepakketten
            </h2>
            <VRow>
              <VCol
                v-for="(pkg, index) in servicePackages"
                :key="index"
                cols="12"
                md="4"
              >
                <VCard
                  variant="outlined"
                  class="h-100"
                >
                  <VCardTitle class="text-center bg-primary pa-4">
                    <span class="text-h5 text-white">{{ pkg.name }}</span>
                  </VCardTitle>
                  <VCardText class="pa-4">
                    <div class="mb-3">
                      <strong>Beschikbaarheid:</strong> {{ pkg.availability }}
                    </div>
                    <div class="mb-3">
                      <strong>Reactietijd:</strong> {{ pkg.responseTime }}
                    </div>
                    <div class="mb-3">
                      <strong>Support:</strong> {{ pkg.support }}
                    </div>
                    <div class="mb-3">
                      <strong>Onderhoudsvenster:</strong> {{ pkg.maintenanceWindow }}
                    </div>
                    <VDivider class="my-4" />
                    <div class="mb-2">
                      <strong>Kenmerken:</strong>
                    </div>
                    <ul>
                      <li
                        v-for="(feature, fIndex) in pkg.features"
                        :key="fIndex"
                        class="mb-2"
                      >
                        {{ feature }}
                      </li>
                    </ul>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </div>

          <!-- Incident Priorities -->
          <div class="mb-8">
            <h2 class="text-h6 mb-4">
              5. Beschikbaarheid en uptime
            </h2>
            <p class="text-body-1 mb-4">
              De uptime wordt gemeten per kalendermaand. De Dienstverlener streeft naar de volgende beschikbaarheid per servicepakket:
              <br>• Basic: 95% uptime (maximaal 36 uur downtime per maand)
              <br>• Professional: 98% uptime (maximaal 14.5 uur downtime per maand)
              <br>• Enterprise: 99.5% uptime (maximaal 3.5 uur downtime per maand)
            </p>
          </div>

          <div class="mb-8">
            <h2 class="text-h6 mb-4">
              6. Incidentbeheer en reactietijden
            </h2>
            <VTable>
              <thead>
                <tr>
                  <th>Prioriteit</th>
                  <th>Beschrijving</th>
                  <th>Reactie Basic</th>
                  <th>Reactie Pro</th>
                  <th>Reactie Enterprise</th>
                  <th>Oplossing Basic</th>
                  <th>Oplossing Pro</th>
                  <th>Oplossing Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(incident, index) in incidentPriorities"
                  :key="index"
                >
                  <td><strong>{{ incident.priority }}</strong></td>
                  <td>{{ incident.description }}</td>
                  <td>{{ incident.responseBasic }}</td>
                  <td>{{ incident.responsePro }}</td>
                  <td>{{ incident.responseEnterprise }}</td>
                  <td>{{ incident.resolutionBasic }}</td>
                  <td>{{ incident.resolutionPro }}</td>
                  <td>{{ incident.resolutionEnterprise }}</td>
                </tr>
              </tbody>
            </VTable>
            <p class="text-body-2 mt-3">
              * Reactietijd = Tijd tot eerste reactie | Oplossingstijd = Geschatte tijd tot oplossing (best effort)
            </p>
          </div>

          <!-- Additional sections -->
          <div
            v-for="(section, index) in additionalSections"
            :key="`add-${index}`"
            class="mb-6"
          >
            <h2 class="text-h6 mb-3">
              {{ section.title }}
            </h2>
            <p
              class="text-body-1"
              style="white-space: pre-line;"
            >
              {{ section.content }}
            </p>
          </div>

          <VDivider class="my-6" />

          <VAlert
            type="info"
            variant="tonal"
            class="mb-6"
          >
            <strong>Let op:</strong> Dit is een standaard SLA template. De exacte serviceniveaus, reactietijden en voorwaarden worden overeengekomen in de specifieke dienstverleningsovereenkomst met de klant.
          </VAlert>

          <div class="text-center">
            <VBtn
              to="/"
              color="primary"
            >
              Terug naar Home
            </VBtn>
          </div>
        </VContainer>
      </VCardText>
    </VCard>
  </VContainer>
</template>
